import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, Download, Eye, FileSpreadsheet, Upload, X } from 'lucide-react';
import apiClient from '../api/client';
import { useToast, type ToastTone } from './ui/Toast';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { inputClass, checkboxClass } from './ui/Input';

type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning'
  | 'muted';

export interface ImportPreviewRow {
  line: number;
  ref: string;
  status: string;
  message?: string;
  [k: string]: unknown;
}

interface PreviewResponse {
  total: number;
  ignoredColumns?: string[];
  rows: ImportPreviewRow[];
}

export interface ImportDialogMode {
  value: string;
  label: string;
  hint?: string;
}

export interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful commit so the caller can reload its list. */
  onDone: () => void;
  title: string;
  description?: string;
  /** Label for the mode selector, e.g. "Import mode" or "Movement type". */
  modeLabel: string;
  modes: ImportDialogMode[];
  /** Header line for the downloadable CSV template (the standardized columns). */
  templateHeader: string;
  templateName: string;
  /**
   * Optional worked example offered next to the blank template: the same columns, already
   * filled with rows that resolve. A header-only template shows the shape but not the
   * conventions (which category lane to use, what a code looks like), and that is exactly
   * where a hand-prepared file goes wrong.
   */
  exampleRows?: string[];
  exampleName?: string;
  previewEndpoint: string;
  commitEndpoint: string;
  /** Build the request body for the selected mode + uploaded bytes. */
  buildBody: (mode: string, fileBase64: string) => Record<string, unknown>;
  statusVariant: (status: string) => BadgeVariant;
  renderDetail: (row: ImportPreviewRow) => ReactNode;
  /** Whether a row of this status will actually be written on commit (enables the Confirm button). */
  actionable: (status: string) => boolean;
  summarizeResult: (data: unknown) => { tone: ToastTone; title: string; message: string };
  /** NEW-products only: offer a "TecDoc auto-enrich" toggle. */
  showAutoEnrich?: boolean;
  /**
   * Caller-supplied controls rendered above the drop zone, receiving the selected mode so they
   * can hide for modes they don't apply to. Used for the catalog import's file-level defaults
   * (supplier / coefficient / category), which belong to the file rather than to any one row.
   * The values themselves reach the request through the caller's own `buildBody`.
   */
  extraControls?: (mode: string) => ReactNode;
}

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Two-phase file import: drop a file → preview every row's verdict (writes nothing) → Confirm to
 * commit. Shared by the catalog product import and the Purchasing stock import; the mode selector,
 * preview columns, and result summary are supplied by the caller.
 */
export function ImportDialog(props: ImportDialogProps) {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const [mode, setMode] = useState(props.modes[0].value);
  const [autoEnrich, setAutoEnrich] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!props.open) return null;

  const reset = () => {
    setFile(null);
    setPreview(null);
    setBusy(null);
  };

  const close = () => {
    reset();
    props.onClose();
  };

  const pickFile = (f: File | null) => {
    setFile(f);
    setPreview(null); // a new file invalidates the prior preview
  };

  // Caller's body, with the autoEnrich flag merged in when this variant offers it (catalog NEW).
  const buildBody = (b64: string) => {
    const base = props.buildBody(mode, b64);
    return props.showAutoEnrich ? { ...base, autoEnrich } : base;
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy('preview');
    try {
      const fileBase64 = await fileToBase64(file);
      // Preview is read-only, but the routers sit behind the idempotency guard which requires an
      // X-Command-Id on every POST — send a fresh one (replay-caching a preview is harmless).
      const { data } = await apiClient.post<PreviewResponse>(
        props.previewEndpoint,
        buildBody(fileBase64),
        {
          headers: { 'X-Command-Id': crypto.randomUUID() },
        }
      );
      setPreview(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  const runCommit = async () => {
    if (!file) return;
    setBusy('commit');
    try {
      const fileBase64 = await fileToBase64(file);
      const { data } = await apiClient.post(props.commitEndpoint, buildBody(fileBase64), {
        headers: { 'X-Command-Id': crypto.randomUUID() },
      });
      push(props.summarizeResult(data));
      props.onDone();
      close();
    } catch (err) {
      showApiError(err);
      setBusy(null);
    }
  };

  const downloadCsv = (lines: string[], filename: string) => {
    // BOM first: Excel opens a plain UTF-8 CSV as Latin-1 and turns "Filtre à huile" into
    // "Filtre Ã  huile" — which is precisely the category name a client would then fail to match.
    const blob = new Blob(['﻿' + lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => downloadCsv([props.templateHeader], props.templateName);
  const downloadExample = () =>
    downloadCsv(
      [props.templateHeader, ...(props.exampleRows ?? [])],
      props.exampleName ?? 'example.csv'
    );

  const actionableCount = preview?.rows.filter((r) => props.actionable(r.status)).length ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={close} aria-hidden />
      <div className="relative flex max-h-[88vh] w-full max-w-4xl flex-col rounded-lg bg-background shadow-xl">
        <header className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">{props.title}</h2>
            {props.description && (
              <p className="text-xs text-muted-foreground">{props.description}</p>
            )}
          </div>
          <button
            onClick={close}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label={t('import.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {/* Mode + template */}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                {props.modeLabel}
              </label>
              <select
                value={mode}
                onChange={(e) => {
                  setMode(e.target.value);
                  setPreview(null);
                }}
                className={inputClass + ' w-auto'}
              >
                {props.modes.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              {props.modes.find((m) => m.value === mode)?.hint && (
                <p className="mt-1 max-w-md text-xs text-muted-foreground">
                  {props.modes.find((m) => m.value === mode)!.hint}
                </p>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="h-4 w-4" /> {t('import.downloadTemplate')}
              </Button>
              {props.exampleRows && props.exampleRows.length > 0 && (
                <Button variant="outline" size="sm" onClick={downloadExample}>
                  <Download className="h-4 w-4" /> {t('import.filledExample')}
                </Button>
              )}
            </div>
          </div>

          {props.showAutoEnrich && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={autoEnrich}
                onChange={(e) => setAutoEnrich(e.target.checked)}
                className={checkboxClass}
              />
              {t('import.autoEnrich')}
            </label>
          )}

          {props.extraControls?.(mode)}

          {/* Drop zone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const f = e.dataTransfer.files?.[0];
              if (f) pickFile(f);
            }}
            onClick={() => inputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
              dragging ? 'border-primary bg-primary/5' : 'border-input hover:bg-accent/40'
            }`}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickFile(f);
                e.target.value = '';
              }}
            />
            {file ? (
              <span className="inline-flex items-center gap-2 text-sm">
                <FileSpreadsheet className="h-4 w-4 text-primary" /> {file.name}
              </span>
            ) : (
              <>
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-medium">{t('import.dropHint')}</span>
                <span className="text-xs text-muted-foreground">{t('import.dropFormats')}</span>
              </>
            )}
          </div>

          {file && !preview && (
            <Button onClick={() => void runPreview()} disabled={busy !== null}>
              <Eye className="h-4 w-4" />
              {busy === 'preview' ? t('import.validating') : t('import.validate')}
            </Button>
          )}

          {/* Preview */}
          {preview && (
            <div className="space-y-3">
              {preview.ignoredColumns && preview.ignoredColumns.length > 0 && (
                <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                  {t('import.ignoredColumns', { columns: preview.ignoredColumns.join(', ') })}
                </p>
              )}
              <SummaryBar rows={preview.rows} statusVariant={props.statusVariant} />
              <div className="max-h-[40vh] overflow-auto rounded-md border">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="px-3 py-2 font-medium">#</th>
                      <th className="px-3 py-2 font-medium">{t('import.reference')}</th>
                      <th className="px-3 py-2 font-medium">{t('common.status')}</th>
                      <th className="px-3 py-2 font-medium">{t('import.details')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr key={row.line} className="border-t">
                        <td className="px-3 py-1.5 text-xs text-muted-foreground">{row.line}</td>
                        <td className="px-3 py-1.5 font-mono text-xs">
                          <div className="truncate max-w-[18rem]" title={row.ref || undefined}>
                            {row.ref || '—'}
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <Badge variant={props.statusVariant(row.status)}>{row.status}</Badge>
                        </td>
                        <td className="px-3 py-1.5 text-xs">
                          {props.renderDetail(row)}
                          {row.message && (
                            <div
                              className="text-muted-foreground truncate max-w-[18rem]"
                              title={row.message}
                            >
                              {row.message}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {preview && (
          <footer className="flex items-center justify-between gap-3 border-t px-5 py-3">
            <span className="text-xs text-muted-foreground">
              {actionableCount > 0
                ? t('import.willApply', { count: actionableCount })
                : t('import.nothingToApply')}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setPreview(null)}>
                <ArrowLeft className="h-4 w-4" />
                {t('import.back')}
              </Button>
              <Button
                size="sm"
                onClick={() => void runCommit()}
                disabled={busy !== null || actionableCount === 0}
              >
                <Check className="h-4 w-4" />
                {busy === 'commit' ? t('import.importing') : t('import.confirm')}
              </Button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}

function SummaryBar({
  rows,
  statusVariant,
}: {
  rows: ImportPreviewRow[];
  statusVariant: (s: string) => BadgeVariant;
}) {
  const { t } = useTranslation();
  const counts = new Map<string, number>();
  for (const r of rows) counts.set(r.status, (counts.get(r.status) ?? 0) + 1);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground">
        {t('import.rowsLabel', { count: rows.length })}
      </span>
      {[...counts.entries()].map(([status, n]) => (
        <Badge key={status} variant={statusVariant(status)}>
          {n} {status}
        </Badge>
      ))}
    </div>
  );
}
